import styled from '@emotion/styled';

import type { FieldLabelProps, FontSizeValue } from './FieldLabel.types';
import type { Theme } from '../../theme';

/**
 * Map theme font size token to actual value
 */
const getFontSizeValue = (fontSize: FontSizeValue | undefined, theme: Theme): string => {
  if (!fontSize) {
    return theme.typography.fontSize.sm;
  }
  
  // Check if it's a theme token key
  const themeTokens = ['xs', 'sm', 'base', 'md', 'lg', 'xl'] as const;
  if (themeTokens.includes(fontSize as typeof themeTokens[number])) {
    return theme.typography.fontSize[fontSize as keyof typeof theme.typography.fontSize] || theme.typography.fontSize.sm;
  }
  
  // Otherwise, treat as CSS value
  return typeof fontSize === 'number' ? `${fontSize}px` : String(fontSize);
};

/**
 * Container for the field label
 */
const Container = styled.div`
  padding-bottom: 4px;
`;

/**
 * The label element with proper typography
 */
const Label = styled.label<{ $fontSize?: FontSizeValue }>`
  font-family: ${({ theme }) => theme.typography.fontFamily};
  font-size: ${({ theme, $fontSize }) => getFontSizeValue($fontSize, theme)};
  font-weight: ${({ theme }) => theme.typography.fontWeight.semibold};
  line-height: ${({ theme }) => theme.typography.lineHeight.normal};
  color: ${({ theme }) => theme.colors.grey[900]};
  display: flex;
  align-items: center;
`;

/**
 * Required indicator asterisk
 */
const RequiredIndicator = styled.span`
  color: ${({ theme }) => theme.colors.highlight};
  margin-left: 2px;
`;

/**
 * FieldLabel Component
 *
 * A label component for form fields with optional required indicator.
 * Follows accessibility best practices by properly associating with form elements.
 *
 * @example
 * ```tsx
 * <FieldLabel label="Email" htmlFor="email-input" required />
 * <FieldLabel label="Large Label" fontSize="lg" />
 * <FieldLabel label="Custom Size" fontSize="18px" />
 * ```
 */
export function FieldLabel({
  label,
  required = false,
  htmlFor,
  id,
  className,
  fontSize,
  'data-testid': testId,
  dataTestId,
  dataId,
}: FieldLabelProps) {
  if (!label || label.trim() === '') {
    return null;
  }

  return (
    <Container className={className} data-testid={dataTestId || testId} data-id={dataId}>
      <Label htmlFor={htmlFor} id={id} $fontSize={fontSize}>
        {label}
        {required && <RequiredIndicator aria-hidden="true">*</RequiredIndicator>}
      </Label>
    </Container>
  );
}

export default FieldLabel;
